import type { ApolloQueryResult } from '@apollo/client/core'
import { type AvailabilityDatesResponse, fetchAvailabilityDates } from 'customer-flows/gql/queries/availability-dates'
import { fetchAvailabilityTimes } from 'customer-flows/gql/queries/availability-times'
import Logger from 'customer-flows/utils/logger'
import { QueryCache } from 'customer-flows/utils/core/query-cache'
import type { FetchDims } from './keys'
import type { DatesEntry, TimesEntry } from './types'
import type { Scopes } from './scopes'
import { keySet } from './scopes'

// 5 minutes: how long a fetched entry is served as-is before a read triggers a background refetch.
const DEFAULT_STALE_TIME = 5 * 60 * 1000
// 10 minutes: how long an entry may go unread before the eviction sweep reclaims it as cold.
const DEFAULT_GC_TIME = 10 * 60 * 1000
// Upper bound on resident entries; a burst past this evicts least-recently-read UNPINNED entries.
const DEFAULT_MAX_ENTRIES = 50

export type EngineConfig = {
  staleTime?: number
  gcTime?: number
  maxEntries?: number
  fetchFn?: typeof fetchAvailabilityDates
  timesFetchFn?: typeof fetchAvailabilityTimes
  /**
   * Whether a settled month speculatively preloads the next one. Default true.
   *
   * A kill switch for the speculation, in the same category as `staleTime` and `maxEntries`: it
   * tunes how much work the engine does ahead of being asked, and changes no answer it gives.
   *
   * It exists because "don't preload" and "a scrub is in progress" became the same condition in
   * step 2C — `isScrubbing` is now derived from `anchoredDateRange !== dateRange` rather than
   * passed, so the only way to express "no preload" through args is to also move the fetch scope
   * off the visible range. Anything that wants one without the other needs this knob.
   *
   * Applied in `scopes.ts`, not here: turning it off EMPTIES the look-ahead scope, exactly as
   * scrubbing does. Skipping only the fetch would leave those keys pinned, so they would hold
   * eviction budget for entries that are never going to exist.
   */
  lookAhead?: boolean
}

// A response only carries dates on the success typename; anything else contributes nothing.
function extractDates(response: ApolloQueryResult<AvailabilityDatesResponse> | undefined): AvailabilityDate[] {
  return response?.data?.availabilityDates.__typename === 'AvailabilityDatesSuccess'
    ? response.data.availabilityDates.availabilityDates
    : []
}

// The times query is loosely typed (see gql/queries/availability-times.ts) — a response only
// ever carries a times array on `data.availabilityTimes`.
function extractTimes(response: any): AvailabilityTime[] {
  return response?.data?.availabilityTimes ?? []
}

// The query variables a key's dims describe, in the shape both queries take.
function queryVariablesFor({ variables }: FetchDims) {
  return {
    activityId: variables.activityId!,
    dateRange: variables.dateRange,
    resourceOptionQuantities: variables.resourceOptionQuantities,
    ...(variables.channelId && { channelId: variables.channelId })
  }
}

/**
 * The two query caches and the fetch scheduling over them — the adapter layer between the
 * domain-blind mechanism in `utils/core/query-cache.ts` and this domain's policy. Everything the
 * caches call back into (`fetch`, `hasData`, `emptyData`, `shouldFetch`, `onSettled`, `pinnedKeys`)
 * is availability policy; the entry map, freshness, dedupe, gc/LRU and invalidation are not.
 */
export function createCaches(
  ctx: object,
  config: EngineConfig,
  { keyScopes, alive }: { keyScopes: () => Scopes; alive: () => boolean }
) {
  const fetchFn = config.fetchFn ?? fetchAvailabilityDates
  const timesFetchFn = config.timesFetchFn ?? fetchAvailabilityTimes

  let lastError: unknown = null

  // Settings both caches share. Freshness, eviction budget and the "nothing to ask yet" guard are
  // the same policy for both families; only the transport and the payload shape differ.
  //
  // NOTE: `maxEntries` is now a PER-CACHE bound, so the engine's total residency is up to twice
  // what one tagged map held. That is the point — the dates families are many keys (weeks x
  // quantity/seed) while times mints at most one key per selected date/quantity combination, and
  // under one map the dates pinned set could starve times entries out.
  //
  // KNOWN LIMITATION: `runEviction`'s loop condition counts the whole map while the candidate scan
  // only considers EVICTABLE entries. Once the pinned set alone reaches `maxEntries`, every
  // unpinned entry is dropped on every sweep and the cache degenerates to "current scope only",
  // refetching previously-visited months rather than retaining them to `gcTime`. Ordinary
  // two-month navigation lands ~20 entries and is unaffected; the worst case (post-ticket AND
  // cross-month selected date AND mid-scrub) reaches ~43-51 against a default cap of 50. Budgeting
  // the loop over `countEvictable(pinned)` would fix it, but that changes the residency bound from
  // `maxEntries` to `pinned + maxEntries` — a product call, deliberately not taken here.
  const shared = {
    staleTime: config.staleTime ?? DEFAULT_STALE_TIME,
    gcTime: config.gcTime ?? DEFAULT_GC_TIME,
    maxEntries: config.maxEntries ?? DEFAULT_MAX_ENTRIES,
    revalidateOnVisible: true,
    // No activity id yet — nothing to fetch; leave any prior data in place.
    shouldFetch: ({ variables }: FetchDims) => !!variables.activityId
  }

  const datesCache = new QueryCache<FetchDims, AvailabilityDate[]>(ctx, {
    ...shared,
    fetch: async (dims) => extractDates(await fetchFn(ctx, queryVariablesFor(dims))),
    hasData: (dates) => dates.length > 0,
    emptyData: () => [],
    // `.error` is a DATES-only signal, which is why only this cache writes it. A background times
    // fetch settling must not clobber a dates-fetch error that's on screen — with both families
    // racing on every read, a times fetch used to wipe the surfaced error non-deterministically.
    // Under one tagged cache that took a `family` check inside onSettled; with two it is structural.
    onSettled: (_key, { variables }, error) => {
      if (error) {
        lastError = error
        Logger.error('Availability engine failed to fetch dates', { error, dateRange: variables.dateRange })
        return
      }
      lastError = null
    },
    // Never evict a key any scope still wants — that forces an immediate refetch, and at the cap it
    // becomes an evict->refetch->evict thrash. Look-ahead is pinned for the opposite reason: those
    // entries are by construction the least recently read, so unpinned they'd be the FIRST victims
    // and the preload could be discarded before the user ever paged into it.
    pinnedKeys: () => {
      const { visible, anchored, lookAhead } = keyScopes()
      return keySet(visible.dates, anchored.dates, lookAhead.dates)
    }
  })

  // One family only — the selected date's quantity-specific times, which IS what the user is
  // waiting on, so it goes out at normal priority. The `lowPriority` header existed for the
  // retired all-type background seed and has no remaining caller.
  const timesCache = new QueryCache<FetchDims, AvailabilityTime[]>(ctx, {
    ...shared,
    fetch: async (dims) => extractTimes(await timesFetchFn(ctx, queryVariablesFor(dims))),
    hasData: (times) => times.length > 0,
    emptyData: () => [],
    onSettled: (_key, { variables }, error) => {
      if (error) Logger.error('Availability engine failed to fetch times', { error, dateRange: variables.dateRange })
    },
    pinnedKeys: () => {
      const { visible, anchored } = keyScopes()
      return keySet(visible.times, anchored.times)
    }
  })

  // An entry contributes to the projection as long as it still carries data — so stale-,
  // revalidating- (loading), and errored entries keep showing their last-known data rather than
  // blanking out. A never-loaded entry (empty, not yet succeeded) contributes nothing. A
  // hard-invalidated entry is unusable everywhere until a fresh response replaces it, so it
  // vanishes from the projection and every other consumer of the entries map.
  const usableDates = (entry: DatesEntry | undefined): entry is DatesEntry => datesCache.isUsable(entry)
  const usableTimes = (entry: TimesEntry | undefined): entry is TimesEntry => timesCache.isUsable(entry)

  /**
   * THE fetch-scheduling policy — the one place `ensure`/`ensureDeferred` is called. A priority
   * ladder: dates are what the calendar gates on, so they go out first and win the connection slot;
   * the selected-date times fetch and the look-ahead both hang off that same gate so neither can
   * precede or starve them. Deferring times is what the dates seed's nested `availabilityTimes`
   * pay for: the pickers render that optimistic answer while the authoritative times fetch queues.
   *
   * `includeLookAhead` is false at mount: speculating a month ahead before the current month has
   * even been asked for would put the preload in front of what the user is waiting on.
   */
  function ensureScopes({ anchored, lookAhead }: Scopes, { includeLookAhead }: { includeLookAhead: boolean }): void {
    datesCache.ensure(anchored.dates)

    // The gate spans the two caches, so it is resolved as promises on the DATES cache and handed to
    // whichever cache owns the deferred keys.
    const gatingOnDates = datesCache.pendingFor(anchored.dates)

    timesCache.ensureDeferred(anchored.times, gatingOnDates)
    if (includeLookAhead) datesCache.ensureDeferred(lookAhead.dates, gatingOnDates)
  }

  /**
   * Schedules the fetches off the render pass.
   *
   * This is what lets the projection be a pure derivation. Scheduling used to live inside the
   * projection's `createCache` compute, so reading `availabilityDates.value` — from a store getter,
   * a template binding, anywhere — issued network requests as a side effect.
   *
   * A read still triggers this, because stale-while-revalidate needs it to: staleness is a function
   * of elapsed time, and there is no push-based autotrack effect to re-run scheduling when an entry
   * crosses `staleTime`. Deferring to a microtask is what makes that safe — nothing writes tracked
   * state during the render pass that triggered it.
   *
   * The scopes are resolved SYNCHRONOUSLY here, and only the `ensure` calls are deferred. Resolving
   * them on the microtask instead would re-read `args()` a turn late and mint keys from whatever
   * the args had drifted to, breaking the contract that a key carries the variables its own fetch
   * will send. Every read's snapshot is queued and drained, so a turn with two reads at different
   * args still ensures both — `ensure` is idempotent for fresh and in-flight keys.
   *
   * DISTINCT snapshots, though. `keyScopes()` is memoized against its args, so every read within
   * one pass hands back the same object identity, and a turn with N reads at unchanged args used to
   * queue N identical snapshots and replay `ensureScopes` over all of them — one date click drained
   * 3,520. Deduping by identity keeps the "two reads at different args ensure both" property (drift
   * mints a new object) while collapsing the replay to one pass per distinct scope.
   */
  const pendingScopes = new Set<Scopes>()
  let fetchesScheduled = false
  function scheduleFetches(): void {
    if (!alive()) return
    pendingScopes.add(keyScopes())

    if (fetchesScheduled) return
    fetchesScheduled = true
    void Promise.resolve().then(() => {
      fetchesScheduled = false
      const snapshots = [...pendingScopes]
      pendingScopes.clear()
      if (!alive()) return
      for (const snapshot of snapshots) ensureScopes(snapshot, { includeLookAhead: true })
    })
  }

  // Ensures the FETCH scope and hands back what is in flight for it. Deliberately the fetch scope,
  // not the display scope: `.promise` must never widen what gets fetched, or every `.promise` read
  // mid-scrub (setSelectedDate, preflight, the booking steps) would ensure the visible flyby month
  // and defeat the debounce entirely.
  //
  // `only` narrows what is AWAITED, never what is fetched — both families are always ensured, so a
  // dates-only wait still warms times in the background.
  function ensureAnchored({ only }: { only?: 'dates' | 'times' } = {}): Promise<void>[] {
    const { anchored } = keyScopes()

    datesCache.ensure(anchored.dates)
    timesCache.ensure(anchored.times)

    return [
      ...(only === 'times' ? [] : datesCache.pendingFor(anchored.dates)),
      ...(only === 'dates' ? [] : timesCache.pendingFor(anchored.times))
    ]
  }

  // Warm the anchored scope at mount — "prefetch = instantiate before read" — so the dates
  // families (including the all-type seed that backs the optimistic fallback) exist before any
  // render happens. Intent, not a read side effect, so it stays synchronous. There are no times
  // keys yet at this point unless a date is already selected.
  ensureScopes(keyScopes(), { includeLookAhead: false })

  return {
    datesCache,
    timesCache,
    usableDates,
    usableTimes,
    scheduleFetches,
    ensureAnchored,
    get lastError() {
      return lastError
    }
  }
}

export type Caches = ReturnType<typeof createCaches>
